import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Routes, Route, NavLink as RouterNavLink, Navigate, useLocation, useParams, Outlet } from 'react-router-dom';
import './styles/cogslog-full.css';
import './App.css';
import api from './services/api';
import { useTracker } from './services/tracker';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import OrgSwitcher from './components/OrgSwitcher';
import UserMenu from './components/UserMenu';
import ProtectedRoute from './components/ProtectedRoute';
import AccessDenied from './pages/AccessDenied';
import OrgResolver from './components/OrgResolver';
import OrgPicker from './pages/OrgPicker';
import ImpersonationBanner from './components/ImpersonationBanner';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import AppFooter from './components/AppFooter';
import TermsReacceptanceModal from './components/TermsReacceptanceModal';
import CookieBanner from './components/CookieBanner';
import BreadcrumbCollector from './components/BreadcrumbCollector';
import FeedbackButton from './components/FeedbackButton';

// Import pages
import Dashboard from './pages/Dashboard';
import InvoiceEntry from './pages/InvoiceEntry';
import InvoicesList from './pages/InvoicesList';
import Analytics from './pages/Analytics';
import Setup from './pages/Setup';
import OCR from './pages/OCR';
import OrgSettings from './pages/OrgSettings';
import Help from './pages/Help';
import SuperAdmin from './pages/SuperAdmin';
import AuditLogViewer from './pages/AuditLogViewer';
import FeedbackAdmin from './pages/FeedbackAdmin';
import Onboarding from './pages/Onboarding';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import RegisterAccess from './pages/RegisterAccess';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AcceptInvitation from './pages/AcceptInvitation';
import VerifyInvoiceEmail from './pages/VerifyInvoiceEmail';

/**
 * SVG icon components for sidebar navigation.
 * Using inline SVG for crisp rendering at any size, consistent styling,
 * and proper color inheritance from the parent nav-link.
 */
const NavIcons = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
    </svg>
  ),
  invoice: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  list: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
  analytics: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  upload: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  orgSettings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  help: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

// Navigation items configuration — relative paths resolve under /org/:slug/
const navItems = [
  { path: 'dashboard', label: 'Dashboard', icon: NavIcons.dashboard },
  { path: 'analytics', label: 'Analytics', icon: NavIcons.analytics },
  { path: 'ocr', label: 'Upload Invoice', icon: NavIcons.upload },
  { path: 'invoice-entry', label: 'Enter Invoice', icon: NavIcons.invoice },
  { path: 'invoices', label: 'All Invoices', icon: NavIcons.list },
  { path: 'setup', label: 'Setup', icon: NavIcons.settings },
  { path: 'help', label: 'Help', icon: NavIcons.help },
];

// Menu Icon Component
function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  );
}

// Close Icon Component
function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}

/**
 * Layout-only app shell with sidebar navigation, header, and <Outlet /> for child routes.
 * Route definitions live in AppWithAuth; this component renders the chrome around them.
 *
 * @param {Object} props
 * @param {Array} props.businesses - List of business objects for the business selector
 * @param {string|number} props.activeBusiness - Currently selected business ID
 * @param {Function} props.setActiveBusiness - Setter for the active business ID
 */
function AppContent({ businesses, activeBusiness, setActiveBusiness }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, activeOrg, isSuperAdmin, adminPath, emailVerified } = useAuth();

  // Close sidebar when route changes (for mobile/tablet)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar when clicking outside on tablet/mobile
  const handleOverlayClick = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="app-layout">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <ImpersonationBanner />
      {emailVerified === false && <EmailVerificationBanner />}
      {/* Top App Bar */}
      <header className="app-header">
        <div className="app-header__left">
          <button
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          >
            {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
          <h1 className="app-header__title">
            <img
              src="/logos/wordmark/cogslog-wordmark-tagline-color.svg"
              alt="COGSLOG"
              className="app-header__logo"
              height="48"
            />
          </h1>
        </div>

        <div className="app-header__right">
          <OrgSwitcher />
          <div className="business-selector">
            <span className="business-selector__label">Business:</span>
            <select
              value={activeBusiness}
              onChange={(e) => setActiveBusiness(e.target.value)}
              aria-label="Select business"
            >
              <option value="">All Businesses</option>
              {businesses.map(business => (
                <option key={business.id} value={business.id}>
                  {business.name}
                </option>
              ))}
            </select>
          </div>
          <UserMenu />
        </div>
      </header>

      {/* Main Layout */}
      <div className="app-main">
        {/* Sidebar Overlay (for tablet/mobile) */}
        <div
          className={`sidebar-overlay ${sidebarOpen ? 'sidebar-overlay--visible' : ''}`}
          onClick={handleOverlayClick}
        />

        {/* Sidebar Navigation — uses relative paths that resolve under /org/:slug/ */}
        <nav className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
          <div className="sidebar__brand">
            <img
              src="/logos/icon/cogslog-icon-color.svg"
              alt="COGSLOG"
              className="sidebar-icon"
            />
            <img
              src="/logos/wordmark/cogslog-wordmark-tagline-knockout.svg"
              alt="COGSLOG"
              className="sidebar-wordmark"
            />
          </div>
          <div className="sidebar__nav">
            {navItems.map(item => (
              <RouterNavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'nav-link--active' : ''}`
                }
              >
                <span className="nav-link__icon">{item.icon}</span>
                <span>{item.label}</span>
              </RouterNavLink>
            ))}
            {activeOrg?.role === 'admin' && (
              <RouterNavLink
                to="org-settings"
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'nav-link--active' : ''}`
                }
              >
                <span className="nav-link__icon">{NavIcons.orgSettings}</span>
                <span>Org Settings</span>
              </RouterNavLink>
            )}
            {isSuperAdmin && adminPath && (
              <RouterNavLink
                to={adminPath}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'nav-link--active' : ''}`
                }
              >
                <span className="nav-link__icon">{NavIcons.admin}</span>
                <span>System Admin</span>
              </RouterNavLink>
            )}
          </div>
        </nav>

        {/* Main Content — child routes render here via Outlet */}
        <main className="content" id="main-content">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav">
        {navItems.slice(0, 5).map(item => (
          <RouterNavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `mobile-nav__item ${isActive ? 'mobile-nav__item--active' : ''}`
            }
          >
            <span className="mobile-nav__icon">{item.icon}</span>
            <span className="mobile-nav__label">{item.label}</span>
          </RouterNavLink>
        ))}
      </nav>

      {/* Legal Footer — appears on all authenticated pages */}
      <AppFooter />

      {/* BreadcrumbCollector — effect-only, records nav/click events for feedback modal */}
      <BreadcrumbCollector />

      {/* FeedbackButton — floating action button for in-app feedback, always on top */}
      <FeedbackButton
        user={user}
        activeOrg={activeOrg}
        activeBusiness={activeBusiness}
        businesses={businesses}
      />
    </div>
  );
}

AppContent.propTypes = {
  businesses: PropTypes.array.isRequired,
  activeBusiness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setActiveBusiness: PropTypes.func.isRequired,
};

/**
 * Catches old bookmarked URLs like /dashboard and redirects to /org/:slug/...
 * If user has an active org with a slug, preserves the current path under the new prefix.
 * Otherwise redirects to / (org picker).
 */
function LegacyRedirect() {
  const location = useLocation();
  const { activeOrg } = useAuth();

  if (activeOrg?.slug) {
    // Strip any /org prefix to avoid creating /org/slug/org
    let subPath = location.pathname || '';
    subPath = subPath.replace(/^\/org\b/, '');
    // Use the remaining path or default to /dashboard
    if (!subPath || subPath === '/') subPath = '/dashboard';
    return <Navigate to={`/org/${activeOrg.slug}${subPath}`} replace />;
  }
  return <Navigate to="/" replace />;
}

/**
 * Inner component that loads businesses only after auth is ready.
 * Owns all route definitions — AppContent is a layout shell with <Outlet />.
 */
function AppWithAuth() {
  const { initialized, authenticated, adminPath, activeOrg } = useAuth();

  // First-party analytics — pageview on route change + engaged-time heartbeat.
  //
  // Mounted HERE rather than in AppContent (where it originally lived) so that
  // coverage is the whole route table, not just the org-scoped app shell.
  // AppContent only wraps /org/:slug/*, which meant login, signup, onboarding,
  // the org picker and the admin console emitted no beacons at all — the
  // signup funnel was entirely invisible in the console. Called before the
  // `!initialized` early return so the hook order stays stable across renders.
  //
  // The admin console's secret URL segment is redacted server-side on ingest
  // (routes/analytics_track.py::_redact_path) so tracking it cannot leak the
  // operator path into analytics_events.
  useTracker();
  const [businesses, setBusinesses] = useState([]);
  const [activeBusiness, setActiveBusiness] = useState('');
  const activeBusinessRef = useRef(activeBusiness);
  useEffect(() => { activeBusinessRef.current = activeBusiness; }, [activeBusiness]);

  // Empty dependency array is intentional — activeBusinessRef (a ref) avoids stale
  // closure while preventing identity changes that would cascade re-renders to all
  // child components receiving loadBusinesses as a prop.
  const loadBusinesses = useCallback(async () => {
    try {
      const response = await api.getBusinesses();
      setBusinesses(response.data);

      const defaultBusiness = response.data.find(b => b.is_default);
      if (defaultBusiness && !activeBusinessRef.current) {
        setActiveBusiness(defaultBusiness.id);
      }
    } catch (error) {
      console.error('Error loading businesses:', error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Only load businesses after auth is initialized and user is authenticated
  useEffect(() => {
    if (initialized && authenticated) {
      loadBusinesses();
    }
  }, [initialized, authenticated, loadBusinesses]);

  // Show loading screen while auth initializes
  if (!initialized) {
    return (
      <div className="app-loading">
        <img
          src="/logos/loader/cogslog-loader-light-md.svg"
          width="48"
          height="108"
          alt="Loading COGSLOG..."
          role="status"
        />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes (always accessible, no auth required) */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/register-access" element={<RegisterAccess />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/verify-invoice-email" element={<VerifyInvoiceEmail />} />

      {/* Root: OrgPicker if authenticated, redirect to login if not */}
      <Route path="/" element={authenticated ? <OrgPicker /> : <Navigate to="/login" replace />} />

      {/* Protected routes — redirect to login if not authenticated */}
      <Route path="/onboarding" element={authenticated ? <Onboarding /> : <Navigate to="/login" replace />} />
      <Route path="/access-denied" element={<AccessDenied />} />

      {/* Org-scoped routes: /org/:slug wraps OrgResolver → AppContent layout → child routes */}
      <Route
        path="/org/:slug"
        element={
          authenticated ? (
            <OrgResolver>
              <AppContent
                businesses={businesses}
                activeBusiness={activeBusiness}
                setActiveBusiness={setActiveBusiness}
              />
            </OrgResolver>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard activeBusiness={activeBusiness} businesses={businesses} />} />
        <Route path="invoice-entry" element={<InvoiceEntry activeBusiness={activeBusiness} />} />
        <Route path="invoices" element={<InvoicesList />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="setup" element={<Setup />} />
        <Route path="ocr" element={<OCR activeBusiness={activeBusiness} />} />
        <Route path="help" element={<Help />} />
        <Route path="org-settings" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <OrgSettings />
          </ProtectedRoute>
        } />
      </Route>

      {/* Super admin: top-level route with minimal header (no org shell) */}
      {adminPath && (
        <Route path={`${adminPath}/audit-log`} element={
          <ProtectedRoute requiredRoles={['super_admin']}>
            <div className="admin-layout">
              <header className="admin-layout__header">
                <img
                  src="/logos/horizontal/cogslog-horizontal-tagline-color.svg"
                  alt="COGSLOG — Your Cost Navigator"
                  className="admin-layout__logo"
                />
                <span className="admin-layout__title">System Admin</span>
                <UserMenu />
              </header>
              <AuditLogViewer />
            </div>
          </ProtectedRoute>
        } />
      )}
      {adminPath && (
        <Route path={`${adminPath}/feedback`} element={
          <ProtectedRoute requiredRoles={['super_admin']}>
            <div className="admin-layout">
              <header className="admin-layout__header">
                <img
                  src="/logos/horizontal/cogslog-horizontal-tagline-color.svg"
                  alt="COGSLOG — Your Cost Navigator"
                  className="admin-layout__logo"
                />
                <span className="admin-layout__title">System Admin</span>
                <UserMenu />
              </header>
              <FeedbackAdmin />
            </div>
          </ProtectedRoute>
        } />
      )}
      {adminPath && (
        <Route path={`${adminPath}/*`} element={
          <ProtectedRoute requiredRoles={['super_admin']}>
            <div className="admin-layout">
              <header className="admin-layout__header">
                <img
                  src="/logos/horizontal/cogslog-horizontal-tagline-color.svg"
                  alt="COGSLOG — Your Cost Navigator"
                  className="admin-layout__logo"
                />
                <span className="admin-layout__title">System Admin</span>
                <UserMenu />
              </header>
              <SuperAdmin />
            </div>
          </ProtectedRoute>
        } />
      )}

      {/* Catch /org without a slug — redirect based on auth state */}
      <Route path="/org" element={authenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />

      {/* Backward compatibility: redirect old flat URLs or redirect to login */}
      <Route path="*" element={authenticated ? <LegacyRedirect /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}

/**
 * Root application component. Wraps content in AuthProvider and Router.
 */
/**
 * Root application component. Wraps content in AuthProvider and Router.
 *
 * TermsReacceptanceModal is rendered inside AuthProvider but outside Router
 * so it overlays the entire viewport regardless of the current route.
 * It self-manages visibility via AuthContext.requiresTermsAcceptance.
 */
function App() {
  return (
    <AuthProvider>
      {/* Blocking gate: shows when user must re-accept updated terms (LEGAL-06) */}
      <TermsReacceptanceModal />
      {/* Cookie consent banner for EU/UK visitors (LEGAL-07, LEGAL-08) */}
      <CookieBanner />
      <Router>
        <AppWithAuth />
      </Router>
    </AuthProvider>
  );
}

export default App;
